import type {
    AttributeAlias,
    AttributeType,
    AttributeValue,
} from '@/api/models';
import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { AttributeTypesEndpoints } from './attributeTypesEndpoints';
import { toAttributeType, toAttributeTypeList } from './attributeTypesMapping';
import type { IAttributeTypesService } from './IAttributeTypesService';
import type {
    AttributeAliasCreateRequest,
    AttributeTypeWriteRequest,
    AttributeValuesOrderRequest,
    AttributeValueWriteRequest,
} from './requests';
import type {
    GetAttributeTypeListResponse,
    GetPendingAttributeValuesResponse,
} from './responses';

type AttributeTypesServiceOptions = {
    baseUrl: string;
};

/**
 * Client for the global attribute dictionary (`/attribute-types`): types,
 * their select values, and per-value aliases, plus the pending-review queue.
 * Every method returns the never-rejecting `ApiResponse<T>` envelope.
 */
export class AttributeTypesService implements IAttributeTypesService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: AttributeTypesServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async getAttributeTypes(): Promise<
        ApiResponse<GetAttributeTypeListResponse>
    > {
        const result = await makeApiRequest<GetAttributeTypeListResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                AttributeTypesEndpoints.list(),
            ),
        );
        return result.success
            ? { success: true, response: toAttributeTypeList(result.response) }
            : result;
    }

    public async getAttributeType(
        id: string,
    ): Promise<ApiResponse<AttributeType>> {
        const result = await makeApiRequest<AttributeType>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                AttributeTypesEndpoints.details(id),
            ),
        );
        return result.success
            ? { success: true, response: toAttributeType(result.response) }
            : result;
    }

    public getPendingValues(): Promise<
        ApiResponse<GetPendingAttributeValuesResponse>
    > {
        return makeApiRequest<GetPendingAttributeValuesResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                AttributeTypesEndpoints.pendingValues(),
            ),
        );
    }

    public createAttributeType(
        body: AttributeTypeWriteRequest,
    ): Promise<ApiResponse<AttributeType>> {
        return makeApiRequest<AttributeType>(
            getAxiosClient({ baseURL: this.baseUrl }).post(
                AttributeTypesEndpoints.create(),
                body,
            ),
        );
    }

    public updateAttributeType(
        id: string,
        body: AttributeTypeWriteRequest,
    ): Promise<ApiResponse<AttributeType>> {
        return makeApiRequest<AttributeType>(
            getAxiosClient({ baseURL: this.baseUrl }).put(
                AttributeTypesEndpoints.update(id),
                body,
            ),
        );
    }

    public deleteAttributeType(id: string): Promise<ApiResponse<void>> {
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(
                AttributeTypesEndpoints.delete(id),
            ),
        );
    }

    public createValue(
        typeId: string,
        body: AttributeValueWriteRequest,
    ): Promise<ApiResponse<AttributeValue>> {
        return makeApiRequest<AttributeValue>(
            getAxiosClient({ baseURL: this.baseUrl }).post(
                AttributeTypesEndpoints.createValue(typeId),
                body,
            ),
        );
    }

    public updateValue(
        typeId: string,
        valueId: string,
        body: AttributeValueWriteRequest,
    ): Promise<ApiResponse<AttributeValue>> {
        return makeApiRequest<AttributeValue>(
            getAxiosClient({ baseURL: this.baseUrl }).put(
                AttributeTypesEndpoints.updateValue(typeId, valueId),
                body,
            ),
        );
    }

    public deleteValue(
        typeId: string,
        valueId: string,
    ): Promise<ApiResponse<void>> {
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(
                AttributeTypesEndpoints.deleteValue(typeId, valueId),
            ),
        );
    }

    public reorderValues(
        typeId: string,
        body: AttributeValuesOrderRequest,
    ): Promise<ApiResponse<AttributeType>> {
        return makeApiRequest<AttributeType>(
            getAxiosClient({ baseURL: this.baseUrl }).put(
                AttributeTypesEndpoints.reorderValues(typeId),
                body,
            ),
        );
    }

    public promoteValue(
        typeId: string,
        valueId: string,
    ): Promise<ApiResponse<AttributeValue>> {
        return makeApiRequest<AttributeValue>(
            getAxiosClient({ baseURL: this.baseUrl }).post(
                AttributeTypesEndpoints.promoteValue(typeId, valueId),
            ),
        );
    }

    public createAlias(
        typeId: string,
        valueId: string,
        body: AttributeAliasCreateRequest,
    ): Promise<ApiResponse<AttributeAlias>> {
        return makeApiRequest<AttributeAlias>(
            getAxiosClient({ baseURL: this.baseUrl }).post(
                AttributeTypesEndpoints.createAlias(typeId, valueId),
                body,
            ),
        );
    }

    public deleteAlias(
        typeId: string,
        valueId: string,
        aliasId: string,
    ): Promise<ApiResponse<void>> {
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(
                AttributeTypesEndpoints.deleteAlias(typeId, valueId, aliasId),
            ),
        );
    }
}
